/**
 * Grand Quack Auto — input system.
 *
 * Captures WASD movement and Space quack into world.input each frame.
 * Edge-triggered quack: true for exactly one fixedUpdate after keydown,
 * then auto-cleared so consumers (quackSystem) see a single pulse.
 *
 * This system has no render hook — all work happens in fixedUpdate or event handlers.
 *
 * See research.md §5 session 2 prompt.
 */

import type { System, World } from '../types';
import { touchEnter, touchMove, touchQuack } from '../ui/touchControls';
import { isMuted, toggleMute } from '../utils/audioMixer';
import { startBackgroundMusic } from '../utils/musicSynth';

// Live keyboard state — populated by window event listeners installed once on init.
const keys: Record<string, boolean> = {};
let quackRequested = false;
let enterRequested = false;
let ragdollRequested = false;
let photoToggleRequested = false;
let photoCaptureRequested = false;
let listenersInstalled = false;

// Mouse delta tracking for photo mode orbit.
export let mouseDeltaX = 0;
export let mouseDeltaY = 0;
let mouseAccumX = 0;
let mouseAccumY = 0;

const installListeners = (): void => {
  if (listenersInstalled) return;
  listenersInstalled = true;

  window.addEventListener('keydown', (e) => {
    // Ignore if user is typing in an input element (Day-20+ chat HUD).
    const target = e.target as HTMLElement | null;
    if (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA') return;

    keys[e.code] = true;
    startBackgroundMusic(); // starts on first keypress (browser autoplay policy)
    if (e.code === 'Space') {
      quackRequested = true;
      e.preventDefault(); // stop page scroll
    }
    if (e.code === 'KeyE') enterRequested = true;
    if (e.code === 'KeyR') ragdollRequested = true;
    if (e.code === 'KeyM') {
      const muted = toggleMute();
      const el = document.getElementById('hud-mute');
      if (el) el.textContent = muted ? '🔇 MUTED' : '';
    }
    if (e.code === 'KeyP') photoToggleRequested = true;
    if (e.code === 'Enter') photoCaptureRequested = true;
  });

  window.addEventListener('keyup', (e) => {
    keys[e.code] = false;
  });

  window.addEventListener('mousemove', (e) => {
    mouseAccumX += e.movementX;
    mouseAccumY += e.movementY;
  });

  // Release all keys on blur — prevents duck walking off into infinity when user alt-tabs.
  window.addEventListener('blur', () => {
    for (const k of Object.keys(keys)) keys[k] = false;
    quackRequested = false;
    enterRequested = false;
  });
};

export const inputSystem: System = {
  name: 'input',
  fixedUpdate(dt: number, world: World) {
    if (!listenersInstalled) installListeners();

    // Map keys → normalized 2D movement vector.
    // y = forward (+W/ArrowUp), x = strafe-right (+D/ArrowRight).
    let x = 0;
    let y = 0;
    if (keys['KeyW'] || keys['ArrowUp']) y += 1;
    if (keys['KeyS'] || keys['ArrowDown']) y -= 1;
    if (keys['KeyD'] || keys['ArrowRight']) x += 1;
    if (keys['KeyA'] || keys['ArrowLeft']) x -= 1;

    // Normalize diagonal movement so it's not sqrt(2) faster than cardinal.
    const len = Math.hypot(x, y);
    if (len > 0) {
      x /= len;
      y /= len;
    }

    // Merge touch input (overrides keyboard if active).
    if (Math.abs(touchMove.x) > 0.01 || Math.abs(touchMove.y) > 0.01) {
      x = touchMove.x;
      y = touchMove.y;
    }
    if (touchQuack) quackRequested = true;
    if (touchEnter) enterRequested = true;

    world.input.move.x = x;
    world.input.move.y = y;

    // Edge-triggered quack — consumers see true for exactly one fixed tick.
    if (quackRequested) {
      world.input.quack = true;
      world.input.lastQuackAt = world.time.elapsed;
      quackRequested = false;
    } else {
      world.input.quack = false;
    }

    // Track Space hold duration for Mega-Quack charge.
    if (keys['Space'] || touchQuack) {
      world.input.quackCharge += dt;
    } else {
      world.input.quackCharge = 0;
    }

    // Edge-triggered vehicle enter/exit.
    world.input.enterVehicle = enterRequested;
    enterRequested = false;

    // R key ragdoll toggle.
    world.input.ragdollToggle = ragdollRequested;
    ragdollRequested = false;

    // Drain mouse delta accumulator.
    mouseDeltaX = mouseAccumX;
    mouseDeltaY = mouseAccumY;
    mouseAccumX = 0;
    mouseAccumY = 0;
  },
};

// Edge-triggered photo mode helpers (consumed by photoModeSystem).
export function consumePhotoToggle(): boolean {
  if (photoToggleRequested) { photoToggleRequested = false; return true; }
  return false;
}
export function consumePhotoCapture(): boolean {
  if (photoCaptureRequested) { photoCaptureRequested = false; return true; }
  return false;
}
